"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import PageSideNavigationLink, { getLastPathSegment } from "./PageSideNavigationLink";
import clsx from "clsx";
import { PageSideNavigationProps } from "./PageSideNavigation";

interface PageSideNavigationClientProps extends PageSideNavigationProps {
  children: React.ReactNode;
}

export default function PageSideNavigationClient({
  navigation,
  activeCategory: activeCategoryProp,
  basePath,
  bannerActive,
  mobileNavigationTitle = "Menu",
  forceNavigationOpen = false,
  children,
}: PageSideNavigationClientProps) {
  const pathname = usePathname();
  const currentSlug = getLastPathSegment(pathname);
  const [open, setOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [openSectionIndex, setOpenSectionIndex] = useState<number | null>(null);
  const [hasUserToggled, setHasUserToggled] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string | string[] | undefined>(activeCategoryProp);
  
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const category = params.get('category');
      setActiveCategory(category || activeCategoryProp);
    }
  }, [pathname, activeCategoryProp]);

  useEffect(() => {
    const resizeHandler = () => {
      setIsMobile(window.innerWidth < 768);
    };
    resizeHandler();

    window.addEventListener("resize", resizeHandler);
    return () => window.removeEventListener("resize", resizeHandler);
  }, []);

  useEffect(() => {
    setHasUserToggled(false);
    setOpenSectionIndex(null);
  }, [pathname, activeCategory]);

  return (
    <aside
      style={{
        height: isMobile ? (open ? "60vh" : "48px") : undefined,
      }}
      className={clsx(
        "lg:w-[calc(322px-32px)] transition-all shrink-0 lg:max-h-[calc(100vh-180px)] lg:h-[calc(100vh-180px)] z-15 flex flex-col lg:justify-between sticky lg:p-0 p-0.5",
        bannerActive ? "lg:top-32 top-[120px]" : "lg:top-24 top-[76px]"
      )}
    >
      <div className="bg-grey-900 absolute w-[calc(100vw-16px)] top-0 bottom-0 left-1/2 -translate-x-1/2 pointer-events-none lg:hidden rounded-lg" />
      <button
        onClick={() => setOpen(!open)}
        className="relative z-10 text-sm font-medium mb-1 py-1 items-center flex gap-2 pl-2.5 mt-2 rounded lg:hidden"
      >
        <div
          className={clsx(
            "w-2.5 h-[13px] aspect-square flex flex-col justify-between relative transition-transform -ml-1.5 mr-0.5",
            open && "text-grey-800"
          )}
        >
          <div
            className={clsx(
              "h-[3px] w-[3px] rounded-full bg-white absolute left-1/2 -translate-x-1/2 transition-all duration-200",
              !open
                ? "opacity-100 top-0"
                : "opacity-0 top-1/2 -translate-y-1/2 right-0"
            )}
          />
          <div className="h-[3px] w-[3px] rounded-full bg-white absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2" />
          <div
            className={clsx(
              "h-[3px] w-[3px] rounded-full bg-white absolute left-1/2 -translate-x-1/2 transition-all duration-200",
              !open
                ? "opacity-100 bottom-0"
                : "opacity-0 bottom-1/2 -translate-y-1/2 left-0"
            )}
          />
        </div>
        {/* {(navigation || []).find((nav) => pathname.includes(nav.))?.title ||
          "Guides"} */}
        {mobileNavigationTitle}
      </button>
      <div
        className={clsx(
          "flex flex-col -ml-1 h-[calc(100dvh-150px)] lg:overflow-y-auto custom-scrollbar items-start relative z-10 px-2 lg:p-0 overflow-y-auto lg:overflow-visible",
          !open && "hidden lg:flex"
        )}
        onClick={() => setOpen(false)}
      >
        {(navigation || []).map((nav, index) => {
          const shouldBeOpen =
            nav.children?.some((child) => child.slug === currentSlug) ||
            nav.slug === activeCategory;

          const isOpen = hasUserToggled 
            ? openSectionIndex === index 
            : shouldBeOpen;

          return (
            <PageSideNavigationLink
              basePath={basePath}
              nav={nav}
              index={index}
              key={`page-side-nav-link-${index}`}
              initialOpen={forceNavigationOpen || shouldBeOpen}
              isMobile={isMobile}
              isOpen={isOpen}
              onToggle={() => {
                setHasUserToggled(true);
                if (isOpen) {
                  setOpenSectionIndex(null);
                } else {
                  setOpenSectionIndex(index);
                }
              }}
              activeCategory={activeCategory}
            />
          );
        })}
        <div className="h-24" />
      </div>
      {children}
    </aside>
  );
}
